import type { ReactNode } from 'react';
import type { ModalProps } from '@mantine/core';
import { acquireZIndex, releaseZIndex } from '../surfaceZIndex';

export interface ModalItem {
  id: string;
  opened: boolean;
  /**
   * Модалка свёрнута в док (MinimizedDock). Контент остаётся смонтированным
   * (keepMounted), поэтому состояние формы сохраняется до разворачивания.
   */
  minimized: boolean;
  /** Модалка развёрнута на весь экран (переключается кнопкой в шапке). */
  fullScreen: boolean;
  content: ReactNode;
  /** z-index поверхности, выданный общим аллокатором при открытии. */
  zIndex: number;
  props: {
    title?: string;
    size?: string | number;
    centered?: boolean;
    fullScreen?: boolean;
    withCloseButton?: boolean;
    /** Показывать кнопку «Свернуть» в шапке модалки. По умолчанию true, отключается false. */
    minimizable?: boolean;
    /** Показывать кнопку «На весь экран» в шапке модалки. По умолчанию true, отключается false. */
    fullScreenable?: boolean;
    /** Запретить закрытие по бекдропу и Esc (крестик остаётся). По умолчанию false. */
    preventClose?: boolean;
    /** Визуальный стиль фона модалки */
    background?: 'default' | 'glassmorphism';
    /** Размер инпутов внутри модалки */
    inputSize?: string;
    /** Пропсы оверлея (Modal.Overlay) */
    overlayProps?: ModalProps['overlayProps'];
    [key: string]: unknown;
  };
  onCloseCallbacks: Array<() => void>;
}

export interface ModalControllerState {
  modals: ModalItem[];
}

export class ModalController {
  private static instance: ModalController;
  private listeners = new Set<() => void>();
  private modals = new Map<string, ModalItem>();
  private state: ModalControllerState = { modals: [] };

  private constructor() {}

  static getInstance(): ModalController {
    if (!ModalController.instance) {
      ModalController.instance = new ModalController();
    }
    return ModalController.instance;
  }

  // Подписка для useSyncExternalStore
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  // Снапшот состояния для useSyncExternalStore
  getSnapshot = (): ModalControllerState => this.state;

  // Уведомление подписчиков об изменениях
  private emit(): void {
    for (const l of this.listeners) l();
  }

  // Обновление состояния
  private updateState(): void {
    const next: ModalControllerState = {
      modals: Array.from(this.modals.values()),
    };

    // Проверяем, изменилось ли состояние (сравниваем только количество и ID)
    // Не используем JSON.stringify, так как ReactNode не сериализуется
    const prevIds = this.state.modals
      .map(m => m.id)
      .sort()
      .join(',');
    const nextIds = next.modals
      .map(m => m.id)
      .sort()
      .join(',');

    if (prevIds === nextIds && this.state.modals.length === next.modals.length) {
      // Проверяем, изменились ли opened/minimized/fullScreen состояния
      const prevOpened = this.state.modals
        .map(m => `${m.id}:${m.opened}:${m.minimized}:${m.fullScreen}`)
        .sort()
        .join(',');
      const nextOpened = next.modals
        .map(m => `${m.id}:${m.opened}:${m.minimized}:${m.fullScreen}`)
        .sort()
        .join(',');
      if (prevOpened === nextOpened) {
        return; // Состояние не изменилось
      }
    }

    this.state = next;
    this.emit();
  }

  // API для изменения состояния
  open(
    id: string,
    content: ReactNode,
    props: ModalItem['props'],
    onCloseCallbacks: Array<() => void>
  ): void {
    // Новая модалка получает z-index из общего аллокатора; при re-open того же id
    // сохраняем ранее выданный z, чтобы не крутить счётчик повторно.
    const existing = this.modals.get(id);
    const zIndex = existing?.zIndex ?? acquireZIndex();
    this.modals.set(id, {
      id,
      opened: true,
      // Повторный open (например, клик по той же кнопке «Редактировать») разворачивает свёрнутую модалку
      minimized: false,
      // При re-open сохраняем текущий fullScreen, для новой — берём из конфига (props.fullScreen)
      fullScreen: existing?.fullScreen ?? Boolean(props.fullScreen),
      content,
      zIndex,
      props,
      onCloseCallbacks,
    });
    this.updateState();
  }

  /** Свернуть модалку в док. Контент остаётся смонтированным, состояние формы сохраняется. */
  minimize(id: string): void {
    const modal = this.modals.get(id);
    if (modal && !modal.minimized) {
      this.modals.set(id, { ...modal, minimized: true });
      this.updateState();
    }
  }

  /** Развернуть свёрнутую модалку обратно из дока. */
  restore(id: string): void {
    const modal = this.modals.get(id);
    if (modal && modal.minimized) {
      this.modals.set(id, { ...modal, minimized: false });
      this.updateState();
    }
  }

  /** Переключить режим «на весь экран». */
  toggleFullScreen(id: string): void {
    const modal = this.modals.get(id);
    if (modal) {
      this.modals.set(id, { ...modal, fullScreen: !modal.fullScreen });
      this.updateState();
    }
  }

  close(id: string): void {
    const modal = this.modals.get(id);
    if (modal) {
      // Выполняем callbacks перед закрытием
      modal.onCloseCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Ошибка в onClose callback:', error);
        }
      });

      // Удаляем модалку из store после закрытия
      this.modals.delete(id);
      releaseZIndex();
      this.updateState();
    }
  }

  update(id: string, updates: Partial<Pick<ModalItem, 'content' | 'props'>>): void {
    const existing = this.modals.get(id);
    if (existing) {
      this.modals.set(id, { ...existing, ...updates });
      this.updateState();
    }
  }

  getModal(id: string): ModalItem | undefined {
    return this.modals.get(id);
  }
}
